import { isEvaluation, unread } from './model.js';
import { compareVersions, count, day, deadline, gib, percent, plural, worst } from './format.js';
import { THRESHOLDS } from './thresholds.js';
const SUPPORT_WARN = THRESHOLDS.supportWarningDays;
/**
 * A version judged against the list the cloud says it supports, with no dates
 * (AKS, IM-216). AKS supports a fixed number of minor versions and drops the
 * oldest when it releases a new one, so being the oldest listed is the warning
 * that a date would otherwise give — without the date, which it publishes only
 * on a web page.
 */
const publishedSupport = (version, published, evidence) => {
    switch (published) {
        case 'unsupported':
            return {
                outcome: 'fail',
                finding: `Kubernetes ${version} is no longer among the versions the cloud supports for this cluster.`,
                recommendation: 'Upgrade the cluster one minor version at a time, through the instance’s own runs: the installer raises it one minor per apply, so several versions behind takes several applies.',
                evidence,
            };
        case 'oldest-supported':
            return {
                outcome: 'warn',
                finding: `Kubernetes ${version} is the oldest version the cloud still supports, so it is the next to leave support.`,
                recommendation: 'It leaves when the cloud releases its next minor version, which it announces rather than publishes as a date here. Plan the upgrade into a change window.',
                evidence,
            };
        case 'supported':
            return {
                outcome: 'ok',
                finding: `Kubernetes ${version} is among the versions the cloud supports, and is not the oldest of them.`,
                evidence,
            };
    }
};
export const clusterSupportCheck = {
    id: 'cluster-support',
    title: 'Kubernetes version is in standard support',
    category: 'managed-services',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const read = unread(facts.clusterSupport, "the cluster's version support");
        if (isEvaluation(read))
            return read;
        const v = read.value;
        const evidence = [
            { label: 'Kubernetes version', value: v.version },
            ...(v.endOfStandardSupport === undefined
                ? []
                : [{ label: 'Standard support ends', value: day(v.endOfStandardSupport) }]),
            ...(v.endOfExtendedSupport === undefined
                ? []
                : [{ label: 'Extended support ends', value: day(v.endOfExtendedSupport) }]),
            ...(v.supportPolicy === undefined
                ? []
                : [
                    {
                        label: 'At the end of standard support',
                        value: v.supportPolicy === 'standard'
                            ? 'the cloud upgrades the cluster itself'
                            : 'the cluster stays, billed at the extended-support rate',
                    },
                ]),
        ];
        if (v.endOfStandardSupport === undefined) {
            return v.published === undefined
                ? {
                    outcome: 'unknown',
                    finding: `The cloud published no support window for Kubernetes ${v.version}.`,
                    evidence,
                }
                : publishedSupport(v.version, v.published, [
                    ...evidence,
                    ...(v.supportPlan === undefined
                        ? []
                        : [{ label: 'Support plan', value: v.supportPlan }]),
                ]);
        }
        const standard = deadline(facts.at, v.endOfStandardSupport, SUPPORT_WARN);
        if (standard.state === 'past') {
            const pastExtended = v.endOfExtendedSupport !== undefined &&
                deadline(facts.at, v.endOfExtendedSupport, 0).state === 'past';
            return {
                outcome: 'fail',
                finding: pastExtended
                    ? `Kubernetes ${v.version} is past the end of extended support.`
                    : `Kubernetes ${v.version} is in extended support, which is billed at several times the standard rate.`,
                recommendation: 'Upgrade the cluster one minor version at a time, through the instance’s own runs, so the next apply does not undo it.',
                evidence,
            };
        }
        if (standard.state === 'soon') {
            return {
                outcome: 'warn',
                finding: `Kubernetes ${v.version} leaves standard support in ${plural(standard.days, 'day')}, on ${day(v.endOfStandardSupport)}.`,
                recommendation: v.supportPolicy === 'standard'
                    ? 'The cloud will upgrade the cluster on that date if nobody does first — plan the upgrade into a change window instead.'
                    : 'From that date the cluster is billed at the extended-support rate. Plan the upgrade before it.',
                evidence,
            };
        }
        return {
            outcome: 'ok',
            finding: `Kubernetes ${v.version} is in standard support until ${day(v.endOfStandardSupport)}.`,
            evidence,
        };
    },
};
/** Statuses in which an add-on is not doing its job, as opposed to changing. */
const BROKEN_ADDON = /^(DEGRADED|CREATE_FAILED|UPDATE_FAILED|DELETE_FAILED)$/;
export const clusterAddonsCheck = {
    id: 'cluster-addons',
    title: 'Cluster add-ons are healthy and current',
    category: 'managed-services',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const read = unread(facts.addons, "the cluster's add-ons");
        if (isEvaluation(read))
            return read;
        const addons = read.value;
        if (addons.length === 0) {
            return {
                outcome: 'not-applicable',
                finding: 'The cluster has no managed add-ons.',
                evidence: [],
            };
        }
        const notes = [];
        const evidence = addons.map((addon) => {
            const behind = addon.defaultVersion !== undefined &&
                compareVersions(addon.version, addon.defaultVersion) < 0;
            if (BROKEN_ADDON.test(addon.status)) {
                notes.push({ outcome: 'fail', text: `${addon.name} is ${addon.status.toLowerCase()}` });
            }
            else if (addon.status !== 'ACTIVE') {
                notes.push({ outcome: 'warn', text: `${addon.name} is ${addon.status.toLowerCase()}` });
            }
            if (behind)
                notes.push({ outcome: 'warn', text: `${addon.name} is behind the default` });
            return {
                label: addon.name,
                value: [
                    addon.version,
                    addon.status,
                    ...(behind && addon.defaultVersion !== undefined
                        ? [`default is ${addon.defaultVersion}`]
                        : []),
                    ...addon.issues.map((i) => `${i.code}: ${i.message}`),
                ].join('; '),
            };
        });
        const outcome = worst(notes.map((n) => n.outcome));
        if (outcome === 'ok') {
            return {
                outcome,
                finding: `${plural(addons.length, 'add-on')}, all active and at or past the default version.`,
                evidence,
            };
        }
        return {
            outcome,
            finding: `${notes.map((n) => n.text).join('; ')}.`,
            recommendation: outcome === 'fail'
                ? 'A degraded add-on usually names its cause among its health issues: most often a missing permission or a configuration value the add-on rejected.'
                : 'Update the add-on to the default for this Kubernetes version before the next cluster upgrade, which expects it.',
            evidence,
        };
    },
};
/** A database's states in which it is serving, lower-cased: RDS's and Azure's. */
const HEALTHY_DATABASE = new Set(['available', 'ready']);
const supportNotes = (at, db) => {
    if (db.endOfStandardSupport === undefined)
        return [];
    const standard = deadline(at, db.endOfStandardSupport, SUPPORT_WARN);
    const name = `${db.engine} ${db.engineVersion}`;
    if (standard.state === 'past') {
        return [
            { outcome: 'fail', text: `${name} is in extended support, which is billed per vCPU-hour` },
        ];
    }
    if (standard.state === 'soon') {
        return [
            {
                outcome: 'warn',
                text: `${name} leaves standard support on ${day(db.endOfStandardSupport)}`,
            },
        ];
    }
    return [];
};
/** Postgres's default `max_connections` for this much memory. */
const defaultMaxConnections = (db) => db.engine.includes('postgres') && db.memoryBytes !== undefined
    ? Math.min(Math.floor(db.memoryBytes / THRESHOLDS.database.postgresBytesPerConnection), THRESHOLDS.database.postgresMaxConnectionsCap)
    : undefined;
const judgeDatabase = (at, db) => {
    const t = THRESHOLDS.database;
    const m = db.metrics;
    const notes = [];
    // RDS says `available`, Azure's flexible server `Ready`; a disabled or
    // dropping flexible server serves nothing (IM-216).
    if (/^(failed|storage-full|incompatible-|disabled|dropping)/i.test(db.status)) {
        notes.push({ outcome: 'fail', text: `${db.id} is ${db.status}` });
    }
    else if (!HEALTHY_DATABASE.has(db.status.toLowerCase())) {
        notes.push({ outcome: 'warn', text: `${db.id} is ${db.status}` });
    }
    if (db.metricsUnreadable !== undefined) {
        notes.push({ outcome: 'unknown', text: `${db.id}'s metrics could not be read` });
    }
    else {
        if (m.cpuAverage !== undefined && m.cpuAverage >= t.cpuFailPercent) {
            notes.push({ outcome: 'fail', text: `${db.id} averages ${percent(m.cpuAverage)} CPU` });
        }
        else if (m.cpuAverage !== undefined && m.cpuAverage >= t.cpuWarnPercent) {
            notes.push({ outcome: 'warn', text: `${db.id} averages ${percent(m.cpuAverage)} CPU` });
        }
        if (m.freeableMemoryMinBytes !== undefined &&
            db.memoryBytes !== undefined &&
            m.freeableMemoryMinBytes < db.memoryBytes * t.freeableMemoryWarnRatio) {
            notes.push({
                outcome: 'warn',
                text: `${db.id}'s freeable memory fell to ${gib(m.freeableMemoryMinBytes)} of ${gib(db.memoryBytes)}`,
            });
        }
        const max = defaultMaxConnections(db);
        if (m.connectionsMax !== undefined && max !== undefined) {
            const ratio = m.connectionsMax / max;
            const text = `${db.id} peaked at ${plural(m.connectionsMax, 'connection')} of about ${count(max)}`;
            if (ratio >= t.connectionsFailRatio)
                notes.push({ outcome: 'fail', text });
            else if (ratio >= t.connectionsWarnRatio)
                notes.push({ outcome: 'warn', text });
        }
        if (m.freeStorageMinBytes !== undefined && db.allocatedStorageBytes !== undefined) {
            const ratio = m.freeStorageMinBytes / db.allocatedStorageBytes;
            const text = `${db.id}'s free storage fell to ${gib(m.freeStorageMinBytes)} of ${gib(db.allocatedStorageBytes)}`;
            if (ratio < t.freeStorageFailRatio)
                notes.push({ outcome: 'fail', text });
            else if (ratio < t.freeStorageWarnRatio)
                notes.push({ outcome: 'warn', text });
        }
    }
    return [...notes, ...supportNotes(at, db)];
};
const databaseLine = (db) => {
    const m = db.metrics;
    const max = defaultMaxConnections(db);
    return {
        label: `Database ${db.id}`,
        value: [
            `${db.instanceClass}, ${db.engine} ${db.engineVersion}, ${db.status}`,
            ...(m.cpuAverage === undefined
                ? []
                : [
                    `CPU ${percent(m.cpuAverage)} average${m.cpuMax === undefined ? '' : `, ${percent(m.cpuMax)} peak`}`,
                ]),
            ...(m.freeableMemoryMinBytes === undefined
                ? []
                : [`freeable memory at least ${gib(m.freeableMemoryMinBytes)}`]),
            ...(m.connectionsMax === undefined
                ? []
                : [
                    `peak ${plural(m.connectionsMax, 'connection')}${max === undefined ? '' : ` of about ${count(max)}`}`,
                ]),
            ...(m.freeStorageMinBytes === undefined
                ? []
                : [`free storage at least ${gib(m.freeStorageMinBytes)}`]),
            ...(db.endOfStandardSupport === undefined
                ? []
                : [`standard support until ${day(db.endOfStandardSupport)}`]),
        ].join('; '),
    };
};
export const databaseCheck = {
    id: 'database',
    title: 'Database has headroom and a supported engine',
    category: 'managed-services',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const read = unread(facts.databases, "the instance's database");
        if (isEvaluation(read))
            return read;
        const dbs = read.value;
        if (dbs.length === 0) {
            return {
                outcome: 'unknown',
                finding: 'No managed database in this region carries the instance’s tag.',
                recommendation: 'An instance installed before its resources were tagged looks like this; otherwise its database is somewhere this cannot see.',
                evidence: [],
            };
        }
        const notes = dbs.flatMap((db) => judgeDatabase(facts.at, db));
        const evidence = dbs.map(databaseLine);
        const outcome = worst(notes.map((n) => n.outcome));
        if (outcome === 'ok') {
            return {
                outcome,
                finding: dbs.length === 1
                    ? 'The database is within every threshold over the last week.'
                    : `All ${String(dbs.length)} databases are within every threshold over the last week.`,
                evidence,
            };
        }
        return {
            outcome,
            finding: `${notes
                .filter((n) => n.outcome !== 'ok')
                .map((n) => n.text)
                .join('; ')}.`,
            ...(outcome === 'unknown'
                ? {}
                : {
                    recommendation: 'A database short of CPU, memory or connections is usually one class too small for the instance’s load; one short of storage needs more provisioned before an import fills it.',
                }),
            evidence,
        };
    },
};
const judgeDomain = (domain) => {
    const t = THRESHOLDS.search;
    const m = domain.metrics;
    if (domain.metricsUnreadable !== undefined) {
        return [{ outcome: 'unknown', text: 'its metrics could not be read' }];
    }
    const notes = [];
    if ((m.redMax ?? 0) >= 1)
        notes.push({ outcome: 'fail', text: 'it was red in the last day' });
    else if ((m.yellowMax ?? 0) >= 1) {
        notes.push({ outcome: 'warn', text: 'it was yellow in the last day' });
    }
    if (m.jvmMemoryPressureMax !== undefined) {
        const text = `JVM memory pressure reached ${percent(m.jvmMemoryPressureMax)}`;
        if (m.jvmMemoryPressureMax >= t.jvmPressureFailPercent)
            notes.push({ outcome: 'fail', text });
        else if (m.jvmMemoryPressureMax >= t.jvmPressureWarnPercent)
            notes.push({ outcome: 'warn', text });
    }
    if (m.freeStorageMinBytes !== undefined && domain.volumeBytes !== undefined) {
        const ratio = m.freeStorageMinBytes / domain.volumeBytes;
        const text = `a node's free storage fell to ${gib(m.freeStorageMinBytes)} of ${gib(domain.volumeBytes)}`;
        if (ratio < t.freeStorageFailRatio)
            notes.push({ outcome: 'fail', text });
        else if (ratio < t.freeStorageWarnRatio)
            notes.push({ outcome: 'warn', text });
    }
    if (m.cpuAverage !== undefined && m.cpuAverage >= t.cpuWarnPercent) {
        notes.push({ outcome: 'warn', text: `CPU averaged ${percent(m.cpuAverage)}` });
    }
    return notes;
};
export const searchDomainCheck = {
    id: 'search-domain',
    title: 'Search domain is green with room to grow',
    category: 'managed-services',
    outcomes: ['ok', 'warn', 'fail', 'unknown', 'not-applicable'],
    evaluate: (facts) => {
        const read = unread(facts.search, "the instance's search domain");
        if (isEvaluation(read))
            return read;
        const { domain, expected } = read.value;
        if (domain === null) {
            return expected
                ? {
                    outcome: 'unknown',
                    finding: 'The instance is configured for a managed search domain, and none in this region carries its tag.',
                    recommendation: 'An instance installed before its resources were tagged looks like this; otherwise its domain is somewhere this cannot see.',
                    evidence: [],
                }
                : {
                    outcome: 'not-applicable',
                    finding: 'No managed search domain carries the instance’s tag, and its configuration does not ask for one.',
                    evidence: [],
                };
        }
        const m = domain.metrics;
        const evidence = [
            {
                label: `Domain ${domain.name}`,
                value: [
                    `${plural(domain.instanceCount, 'node')} of ${domain.instanceType}`,
                    domain.engineVersion,
                    ...(domain.volumeBytes === undefined ? [] : [`${gib(domain.volumeBytes)} each`]),
                ].join(', '),
            },
            ...(m.jvmMemoryPressureMax === undefined
                ? []
                : [{ label: 'JVM memory pressure, peak', value: percent(m.jvmMemoryPressureMax) }]),
            ...(m.freeStorageMinBytes === undefined
                ? []
                : [{ label: 'Free storage, lowest node', value: gib(m.freeStorageMinBytes) }]),
            ...(m.cpuAverage === undefined
                ? []
                : [{ label: 'CPU, average', value: percent(m.cpuAverage) }]),
            ...(domain.metricsUnreadable === undefined
                ? []
                : [{ label: 'Metrics', value: domain.metricsUnreadable }]),
        ];
        const notes = judgeDomain(domain);
        const outcome = worst(notes.map((n) => n.outcome));
        if (outcome === 'ok') {
            return {
                outcome,
                finding: 'The search domain is green and within every threshold.',
                evidence,
            };
        }
        return {
            outcome,
            finding: `The search domain: ${notes.map((n) => n.text).join('; ')}.`,
            ...(outcome === 'unknown'
                ? {}
                : {
                    recommendation: domain.instanceCount === 1 && notes.some((n) => n.text.includes('yellow'))
                        ? 'A one-node domain cannot place replica shards, so it is yellow whenever an index asks for one. Add a node, or set the indexes to no replicas.'
                        : 'Memory pressure and low storage both end in the domain refusing writes. Move to a larger instance type or add storage before ingestion grows further.',
                }),
            evidence,
        };
    },
};
//# sourceMappingURL=managed.js.map